/**
 * Chess Board Class
 * 
 * Manages the chess board representation, initialization, and coordinate conversions.
 * Implements requirements 1.1, 1.2, and 1.5 from the chess-game specification.
 */

import {
  Board as BoardType,
  ChessPiece,
  Position,
  PieceType,
  PlayerColor,
  positionToAlgebraic,
  algebraicToPosition,
  isValidPosition
} from './types';

/**
 * Square color on the chess board
 */
export type SquareColor = 'light' | 'dark';

/**
 * Board class for managing the chess board state
 */
export class Board {
  private board: BoardType;

  /**
   * Creates a new Board instance
   * @param initialBoard Optional initial board state (defaults to empty board)
   */
  constructor(initialBoard?: BoardType) {
    if (initialBoard) {
      this.board = initialBoard;
    } else {
      this.board = this.createEmptyBoard();
    }
  }

  /**
   * Creates an empty 8x8 chess board
   * @returns An 8x8 array filled with null values
   */
  private createEmptyBoard(): BoardType {
    return Array(8).fill(null).map(() => Array(8).fill(null));
  }

  /**
   * Initializes the board with pieces in standard starting positions
   * White pieces on ranks 1-2, black pieces on ranks 7-8
   * Requirement 1.2: Place all 32 pieces in standard starting positions
   */
  public initializeStartingPosition(): void {
    this.board = this.createEmptyBoard();

    // Define the back rank piece order
    const backRankPieces: PieceType[] = [
      'rook', 'knight', 'bishop', 'queen', 'king', 'bishop', 'knight', 'rook'
    ];

    // Place white pieces (ranks 0-1, which represent ranks 1-2 in chess notation)
    for (let file = 0; file < 8; file++) {
      // White back rank (rank 1 in chess notation)
      this.board[0][file] = {
        type: backRankPieces[file],
        color: 'white',
        position: { file, rank: 0 },
        hasMoved: false,
        moveCount: 0
      };

      // White pawns (rank 2 in chess notation)
      this.board[1][file] = {
        type: 'pawn',
        color: 'white',
        position: { file, rank: 1 },
        hasMoved: false,
        moveCount: 0
      };
    }

    // Place black pieces (ranks 6-7, which represent ranks 7-8 in chess notation)
    for (let file = 0; file < 8; file++) {
      // Black pawns (rank 7 in chess notation)
      this.board[6][file] = {
        type: 'pawn',
        color: 'black',
        position: { file, rank: 6 },
        hasMoved: false,
        moveCount: 0
      };

      // Black back rank (rank 8 in chess notation)
      this.board[7][file] = {
        type: backRankPieces[file],
        color: 'black',
        position: { file, rank: 7 },
        hasMoved: false,
        moveCount: 0
      };
    }
  }

  /**
   * Gets the piece at a specific position
   * @param position The position to check
   * @returns The piece at that position, or null if empty
   */
  public getPieceAt(position: Position): ChessPiece | null {
    if (!isValidPosition(position)) {
      return null;
    }
    return this.board[position.rank][position.file];
  }

  /**
   * Sets a piece at a specific position
   * @param position The position to place the piece
   * @param piece The piece to place (or null to clear the square)
   */
  public setPieceAt(position: Position, piece: ChessPiece | null): void {
    if (!isValidPosition(position)) {
      throw new Error(`Invalid position: file=${position.file}, rank=${position.rank}`);
    }
    this.board[position.rank][position.file] = piece;
    
    // Update the piece's position if it's not null
    if (piece) {
      piece.position = { ...position };
    }
  }

  /**
   * Gets the color of a square on the chess board
   * Requirement 1.1: 8x8 board with alternating square colors
   * 
   * In chess, the bottom-left square (a1) is dark.
   * A square is light if (file + rank) is odd, dark if even.
   * 
   * @param position The position to check
   * @returns 'light' or 'dark'
   */
  public getSquareColor(position: Position): SquareColor {
    if (!isValidPosition(position)) {
      throw new Error(`Invalid position: file=${position.file}, rank=${position.rank}`);
    }
    
    // A square is light if the sum of file and rank is odd
    return (position.file + position.rank) % 2 === 1 ? 'light' : 'dark';
  }

  /**
   * Converts a position to algebraic notation
   * Requirement 1.5: Use standard chess board coordinates (files a-h, ranks 1-8)
   * 
   * @param position The position to convert
   * @returns Algebraic notation (e.g., "e4")
   */
  public positionToAlgebraic(position: Position): string {
    return positionToAlgebraic(position);
  }

  /**
   * Converts algebraic notation to a position
   * Requirement 1.5: Use standard chess board coordinates (files a-h, ranks 1-8)
   * 
   * @param algebraic Algebraic notation (e.g., "e4")
   * @returns Position object, or null if invalid
   */
  public algebraicToPosition(algebraic: string): Position | null {
    return algebraicToPosition(algebraic);
  }

  /**
   * Gets the entire board state
   * @returns A copy of the board array
   */
  public getBoard(): BoardType {
    // Return a deep copy to prevent external modification
    return this.board.map(row => [...row]);
  }

  /**
   * Sets the entire board state
   * @param board The new board state
   */
  public setBoard(board: BoardType): void {
    if (board.length !== 8 || !board.every(row => row.length === 8)) {
      throw new Error('Board must be 8x8');
    }
    this.board = board.map(row => [...row]);
  }

  /**
   * Clears the entire board (sets all squares to null)
   */
  public clear(): void {
    this.board = this.createEmptyBoard();
  }

  /**
   * Checks if a position is empty
   * @param position The position to check
   * @returns True if the square is empty, false otherwise
   */
  public isEmpty(position: Position): boolean {
    return this.getPieceAt(position) === null;
  }

  /**
   * Checks if a position contains a piece of a specific color
   * @param position The position to check
   * @param color The color to check for
   * @returns True if the square contains a piece of the specified color
   */
  public hasPieceOfColor(position: Position, color: PlayerColor): boolean {
    const piece = this.getPieceAt(position);
    return piece !== null && piece.color === color;
  }

  /**
   * Finds all pieces of a specific type and color
   * @param type The piece type to find
   * @param color The piece color to find
   * @returns Array of positions where matching pieces are found
   */
  public findPieces(type: PieceType, color: PlayerColor): Position[] {
    const positions: Position[] = [];
    
    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const piece = this.board[rank][file];
        if (piece && piece.type === type && piece.color === color) {
          positions.push({ file, rank });
        }
      }
    }
    
    return positions;
  }

  /**
   * Finds the king of a specific color
   * @param color The color of the king to find
   * @returns The position of the king, or null if not found
   */
  public findKing(color: PlayerColor): Position | null {
    const kings = this.findPieces('king', color);
    return kings.length > 0 ? kings[0] : null;
  }

  /**
   * Counts all pieces on the board
   * @returns Object with counts for white and black pieces
   */
  public countPieces(): { white: number; black: number; total: number } {
    let white = 0;
    let black = 0;
    
    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const piece = this.board[rank][file];
        if (piece) {
          if (piece.color === 'white') white++;
          else black++;
        }
      }
    }
    
    return { white, black, total: white + black };
  }

  /**
   * Creates a copy of this board
   * @returns A new Board instance with the same state
   */
  public clone(): Board {
    const newBoard = new Board();
    newBoard.setBoard(this.getBoard());
    return newBoard;
  }

  /**
   * Validates that the board is in a consistent state
   * @returns True if valid, false otherwise
   */
  public isValid(): boolean {
    // Check board dimensions
    if (this.board.length !== 8 || !this.board.every(row => row.length === 8)) {
      return false;
    }

    // Check that each piece's position matches its location on the board
    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const piece = this.board[rank][file];
        if (piece) {
          if (piece.position.file !== file || piece.position.rank !== rank) {
            return false;
          }
        }
      }
    }

    // Check that there's exactly one king per color
    const whiteKings = this.findPieces('king', 'white');
    const blackKings = this.findPieces('king', 'black');
    
    if (whiteKings.length !== 1 || blackKings.length !== 1) {
      return false;
    }

    return true;
  }

  /**
   * Gets all pieces on the board
   * @returns Array of all pieces with their positions
   */
  public getAllPieces(): ChessPiece[] {
    const pieces: ChessPiece[] = [];
    
    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const piece = this.board[rank][file];
        if (piece) {
          // Ensure the piece's position property matches its actual board position
          // This prevents stale position data
          const pieceWithCorrectPosition: ChessPiece = {
            ...piece,
            position: { rank, file }
          };
          pieces.push(pieceWithCorrectPosition);
        }
      }
    }
    
    return pieces;
  }

  /**
   * Gets all pieces of a specific color
   * @param color The color to filter by
   * @returns Array of pieces of the specified color
   */
  public getPiecesByColor(color: PlayerColor): ChessPiece[] {
    return this.getAllPieces().filter(piece => piece.color === color);
  }

  /**
   * Converts the board to a string representation for debugging
   * @returns String representation of the board
   */
  public toString(): string {
    const pieceSymbols: Record<PieceType, { white: string; black: string }> = {
      king: { white: '♔', black: '♚' },
      queen: { white: '♕', black: '♛' },
      rook: { white: '♖', black: '♜' },
      bishop: { white: '♗', black: '♝' },
      knight: { white: '♘', black: '♞' },
      pawn: { white: '♙', black: '♟' }
    };

    let result = '  a b c d e f g h\n';
    
    for (let rank = 7; rank >= 0; rank--) {
      result += `${rank + 1} `;
      for (let file = 0; file < 8; file++) {
        const piece = this.board[rank][file];
        if (piece) {
          result += pieceSymbols[piece.type][piece.color] + ' ';
        } else {
          result += '. ';
        }
      }
      result += `${rank + 1}\n`;
    }
    
    result += '  a b c d e f g h\n';
    return result;
  }
}
